import { db } from '../firebase';
import logger from '../utils/logger';
import {
  collection, doc, setDoc, getDoc, updateDoc, deleteDoc,
  getDocs, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { DEFAULT_GROUP_KEYS } from '../schemas/defaultGroups';

/**
 * Service for managing family groups in nested structure
 * Groups are stored at: families/{familyCode}/groups/{groupType}/
 * Group types: ADMIN, IMMEDIATE_FAMILY, EXTENDED_FAMILY, etc.
 */
class FamilyGroupService {
  /**
   * Create or update a family group (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type (e.g., 'ADMIN', 'IMMEDIATE_FAMILY')
   * @param {Object} groupData - Group data
   * @returns {Promise<Object>} Created/updated group
   */
  async createFamilyGroup(familyCode, groupType, groupData) {
    if (!familyCode || !groupType) {
      throw new Error('Family code and group type are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);

    const groupDocument = {
      name: groupData.name,
      type: groupType,
      description: groupData.description || '',
      permissions: groupData.permissions || [],
      familyCode: familyCode,
      members: groupData.members || [],
      admins: groupData.admins || [],
      createdBy: groupData.createdBy,
      createdAt: groupData.createdAt || new Date(),
      updatedAt: new Date()
    };

    await setDoc(groupRef, groupDocument);

    return { id: groupType, ...groupDocument };
  }

  /**
   * Get a specific family group (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @returns {Promise<Object>} Group data
   */
  async getFamilyGroup(familyCode, groupType) {
    if (!familyCode || !groupType) {
      throw new Error('Family code and group type are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    return { id: groupDoc.id, ...groupDoc.data() };
  }

  /**
   * Update a family group (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated group
   */
  async updateFamilyGroup(familyCode, groupType, updateData) {
    if (!familyCode || !groupType) {
      throw new Error('Family code and group type are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    await updateDoc(groupRef, {
      ...updateData,
      updatedAt: new Date()
    });

    return { id: groupType, ...groupDoc.data(), ...updateData };
  }

  /**
   * Delete a family group (NESTED STRUCTURE)
   * NOTE: Default groups should not be deleted
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @returns {Promise<void>}
   */
  async deleteFamilyGroup(familyCode, groupType) {
    if (!familyCode || !groupType) {
      throw new Error('Family code and group type are required');
    }

    // Prevent deletion of default groups (use schema as source of truth)
    if (DEFAULT_GROUP_KEYS.includes(groupType)) {
      throw new Error('Cannot delete default groups');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    await deleteDoc(groupRef);
  }

  /**
   * Add a member to a group (NESTED STRUCTURE)
   * Also updates the member's groups array
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {string} memberId - Member ID to add
   * @returns {Promise<void>}
   */
  async addMemberToGroup(familyCode, groupType, memberId) {
    if (!familyCode || !groupType || !memberId) {
      throw new Error('Family code, group type, and member ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    // Add member to group
    await updateDoc(groupRef, {
      members: arrayUnion(memberId),
      updatedAt: new Date()
    });

    // Update member's groups array
    const memberRef = doc(familyRef, 'members', memberId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      await updateDoc(memberRef, {
        groups: arrayUnion(groupType),
        updatedAt: new Date()
      });
    }
  }

  /**
   * Remove a member from a group (NESTED STRUCTURE)
   * Also updates the member's groups array
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {string} memberId - Member ID to remove
   * @returns {Promise<void>}
   */
  async removeMemberFromGroup(familyCode, groupType, memberId) {
    if (!familyCode || !groupType || !memberId) {
      throw new Error('Family code, group type, and member ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    // Remove member from group
    await updateDoc(groupRef, {
      members: arrayRemove(memberId),
      updatedAt: new Date()
    });

    // Update member's groups array
    const memberRef = doc(familyRef, 'members', memberId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      await updateDoc(memberRef, {
        groups: arrayRemove(groupType),
        updatedAt: new Date()
      });
    }
  }

  /**
   * Get all groups for a family (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @returns {Promise<Array>} Array of groups
   */
  async getFamilyGroups(familyCode) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupsCollection = collection(familyRef, 'groups');
    const groupsSnapshot = await getDocs(groupsCollection);

    return groupsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get groups that a member belongs to (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} memberId - Member ID
   * @returns {Promise<Array>} Array of groups the member belongs to
   */
  async getMemberGroups(familyCode, memberId) {
    if (!familyCode || !memberId) {
      throw new Error('Family code and member ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', memberId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      throw new Error('Member not found');
    }

    const memberData = memberDoc.data();
    const memberGroupTypes = memberData.groups || [];

    // Fetch full group data for each group type
    const groups = [];
    for (const groupType of memberGroupTypes) {
      try {
        const group = await this.getFamilyGroup(familyCode, groupType);
        groups.push(group);
      } catch (error) {
        logger.error(`Error fetching group ${groupType}:`, error);
      }
    }

    return groups;
  }

  /**
   * Get all members in a specific group (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @returns {Promise<Array>} Array of member IDs in the group
   */
  async getGroupMembers(familyCode, groupType) {
    if (!familyCode || !groupType) {
      throw new Error('Family code and group type are required');
    }

    const group = await this.getFamilyGroup(familyCode, groupType);
    return group.members || [];
  }

  /**
   * Add a member as a group admin
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {string} memberId - Member ID to make admin
   * @returns {Promise<void>}
   */
  async addGroupAdmin(familyCode, groupType, memberId) {
    if (!familyCode || !groupType || !memberId) {
      throw new Error('Family code, group type, and member ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    // Add member as admin (also ensure they're in members list)
    await updateDoc(groupRef, {
      admins: arrayUnion(memberId),
      members: arrayUnion(memberId),
      updatedAt: new Date()
    });

    // Update member's groups array if not already there
    const memberRef = doc(familyRef, 'members', memberId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      await updateDoc(memberRef, {
        groups: arrayUnion(groupType),
        updatedAt: new Date()
      });
    }
  }

  /**
   * Remove a member as a group admin
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {string} memberId - Member ID to remove as admin
   * @returns {Promise<void>}
   */
  async removeGroupAdmin(familyCode, groupType, memberId) {
    if (!familyCode || !groupType || !memberId) {
      throw new Error('Family code, group type, and member ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      throw new Error('Family group not found');
    }

    // Remove member from admins (they remain in members list)
    await updateDoc(groupRef, {
      admins: arrayRemove(memberId),
      updatedAt: new Date()
    });
  }

  /**
   * Check if a member is in a specific group
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {string} memberId - Member ID to check
   * @returns {Promise<boolean>} True if member is in group
   */
  async isMemberInGroup(familyCode, groupType, memberId) {
    if (!familyCode || !groupType || !memberId) {
      return false;
    }

    try {
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', memberId);
      const memberDoc = await getDoc(memberRef);

      if (!memberDoc.exists()) {
        return false;
      }

      const memberData = memberDoc.data();
      const memberGroups = memberData.groups || [];

      return memberGroups.includes(groupType);
    } catch (error) {
      logger.error('Error checking group membership:', error);
      return false;
    }
  }

  /**
   * Bulk add members to a group
   * @param {string} familyCode - Family code
   * @param {string} groupType - Group type
   * @param {Array<string>} memberIds - Array of member IDs to add
   * @returns {Promise<void>}
   */
  async bulkAddMembersToGroup(familyCode, groupType, memberIds) {
    if (!familyCode || !groupType || !memberIds || memberIds.length === 0) {
      throw new Error('Family code, group type, and member IDs are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const groupRef = doc(familyRef, 'groups', groupType);

    // Add all members to group
    await updateDoc(groupRef, {
      members: arrayUnion(...memberIds),
      updatedAt: new Date()
    });

    // Update each member's groups array
    for (const memberId of memberIds) {
      const memberRef = doc(familyRef, 'members', memberId);
      const memberDoc = await getDoc(memberRef);

      if (memberDoc.exists()) {
        await updateDoc(memberRef, {
          groups: arrayUnion(groupType),
          updatedAt: new Date()
        });
      }
    }
  }
}

export const familyGroupService = new FamilyGroupService();
